import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2 as Grid,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  Paper,
  Divider,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  Build,
  ExpandMore,
  Save,
  Cancel,
  ArrowBack,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadInspectionTemplates,
  saveInspectionTemplate,
} from "../../../../store/actions/ppActions";

const PpInspectionTemplatesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const templates = ppState?.inspections?.templates || [];
  const loading = ppState?.inspections?.loading || false;

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    permitType: "residential",
    inspections: [],
  });

  // Available inspections with dependencies
  const availableInspections = [
    {
      id: "I001",
      name: "Footing/Foundation",
      category: "Foundation",
      dependencies: [],
    },
    {
      id: "I002",
      name: "Slab",
      category: "Foundation",
      dependencies: ["I001"],
    },
    {
      id: "I003",
      name: "Framing",
      category: "Structural",
      dependencies: ["I002"],
    },
    {
      id: "I004",
      name: "Mechanical Rough-In",
      category: "Mechanical",
      dependencies: ["I003"],
    },
    {
      id: "I005",
      name: "Electrical Rough-In",
      category: "Electrical",
      dependencies: ["I003"],
    },
    {
      id: "I006",
      name: "Plumbing Rough-In",
      category: "Plumbing",
      dependencies: ["I003"],
    },
    {
      id: "I007",
      name: "Insulation",
      category: "Energy",
      dependencies: ["I004", "I005", "I006"],
    },
    {
      id: "I008",
      name: "Drywall",
      category: "Interior",
      dependencies: ["I007"],
    },
    { id: "I009", name: "Final", category: "Final", dependencies: ["I008"] },
    {
      id: "I010",
      name: "Fire Suppression",
      category: "Fire Safety",
      dependencies: ["I003"],
    },
    {
      id: "I011",
      name: "Elevator",
      category: "Mechanical",
      dependencies: ["I003"],
    },
    { id: "I012", name: "Pool/Spa", category: "Pool", dependencies: [] },
  ];

  useEffect(() => {
    dispatch(loadInspectionTemplates());
  }, [dispatch]);

  const handleSaveTemplate = async () => {
    try {
      const templateData = {
        ...templateForm,
        id: editingTemplate?.id || undefined,
      };

      await dispatch(saveInspectionTemplate(templateData));
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to save template:", error);
    }
  };

  const handleCloseDialog = () => {
    setTemplateDialogOpen(false);
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      description: "",
      permitType: "residential",
      inspections: [],
    });
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm(template);
    setTemplateDialogOpen(true);
  };

  const handleAddInspection = (inspection) => {
    const newInspection = {
      ...inspection,
      required: true,
      order: templateForm.inspections.length + 1,
    };

    setTemplateForm((prev) => ({
      ...prev,
      inspections: [...prev.inspections, newInspection],
    }));
  };

  const handleRemoveInspection = (inspectionId) => {
    setTemplateForm((prev) => ({
      ...prev,
      inspections: prev.inspections.filter((i) => i.id !== inspectionId),
    }));
  };

  const handleToggleRequired = (inspectionId) => {
    setTemplateForm((prev) => ({
      ...prev,
      inspections: prev.inspections.map((inspection) =>
        inspection.id === inspectionId
          ? { ...inspection, required: !inspection.required }
          : inspection
      ),
    }));
  };

  const moveInspection = (inspectionId, direction) => {
    const currentIndex = templateForm.inspections.findIndex(
      (i) => i.id === inspectionId
    );
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= templateForm.inspections.length) return;

    const newInspections = [...templateForm.inspections];
    [newInspections[currentIndex], newInspections[newIndex]] = [
      newInspections[newIndex],
      newInspections[currentIndex],
    ];

    // Update order numbers
    newInspections.forEach((inspection, index) => {
      inspection.order = index + 1;
    });

    setTemplateForm((prev) => ({
      ...prev,
      inspections: newInspections,
    }));
  };

  const getDependencyNames = (dependencies) => {
    return dependencies
      .map((depId) => availableInspections.find((ai) => ai.id === depId)?.name)
      .filter(Boolean)
      .join(", ");
  };

  const getInspectionsByCategory = () => {
    const categories = {};
    availableInspections.forEach((inspection) => {
      if (!categories[inspection.category]) {
        categories[inspection.category] = [];
      }
      categories[inspection.category].push(inspection);
    });
    return categories;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with back arrow and breadcrumbs */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/inspections")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Inspection Templates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setTemplateDialogOpen(true)}
        >
          New Template
        </Button>
      </Box>

      {/* Templates List */}
      <Grid container spacing={3}>
        {loading ? (
          <Grid size={{ xs: 12 }}>
            <Typography align="center">Loading templates...</Typography>
          </Grid>
        ) : templates.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              No inspection templates created yet. Create templates to
              standardize inspection requirements for different permit types.
            </Alert>
          </Grid>
        ) : (
          templates.map((template) => (
            <Grid key={template.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.permitType}
                        color="primary"
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </Box>
                    <IconButton onClick={() => handleEditTemplate(template)}>
                      <Edit />
                    </IconButton>
                  </Box>

                  {template.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {template.description}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    Inspections ({template.inspections?.length || 0})
                  </Typography>
                  <List dense>
                    {template.inspections
                      ?.slice(0, 3)
                      .map((inspection, index) => (
                        <ListItem key={inspection.id} divider>
                          <ListItemIcon>
                            <Chip
                              label={index + 1}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={inspection.name}
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {inspection.required ? (
                                  <Chip
                                    label="Required"
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    label="Optional"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {inspection.dependencies?.length > 0 && (
                                  <Chip
                                    label={`${inspection.dependencies.length} deps`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    {template.inspections?.length > 3 && (
                      <ListItem>
                        <ListItemText
                          primary={`+${template.inspections.length - 3} more inspections`}
                          sx={{ textAlign: "center", fontStyle: "italic" }}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? "Edit Template" : "Create New Template"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Template Info */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateForm.name}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Permit Type</InputLabel>
                <Select
                  value={templateForm.permitType}
                  label="Permit Type"
                  onChange={(e) =>
                    setTemplateForm((prev) => ({
                      ...prev,
                      permitType: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="mixed_use">Mixed Use</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={templateForm.description}
                onChange={(e) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe when this template should be used..."
              />
            </Grid>

            {/* Available Inspections */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Available Inspections
              </Typography>
              <Paper sx={{ maxHeight: 400, overflow: "auto", p: 1 }}>
                {Object.entries(getInspectionsByCategory()).map(
                  ([category, inspections]) => (
                    <Accordion key={category}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">{category}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {inspections.map((inspection) => {
                            const isAdded = templateForm.inspections.some(
                              (ti) => ti.id === inspection.id
                            );
                            return (
                              <ListItem key={inspection.id}>
                                <ListItemIcon>
                                  <Build fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={inspection.name}
                                  secondary={
                                    inspection.dependencies.length > 0
                                      ? `Depends on: ${getDependencyNames(inspection.dependencies)}`
                                      : "No dependencies"
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <Button
                                    size="small"
                                    variant={isAdded ? "outlined" : "contained"}
                                    disabled={isAdded}
                                    onClick={() =>
                                      handleAddInspection(inspection)
                                    }
                                  >
                                    {isAdded ? "Added" : "Add"}
                                  </Button>
                                </ListItemSecondaryAction>
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </Paper>
            </Grid>

            {/* Selected Inspections */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Template Inspections ({templateForm.inspections.length})
              </Typography>
              {templateForm.inspections.length === 0 ? (
                <Alert severity="info">
                  No inspections added to this template yet. Add inspections
                  from the available list above.
                </Alert>
              ) : (
                <Paper sx={{ p: 2 }}>
                  <List>
                    {templateForm.inspections
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((inspection, index) => (
                        <React.Fragment key={inspection.id}>
                          <ListItem>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <Chip
                                  label={inspection.order || index + 1}
                                  size="small"
                                  color="primary"
                                />
                                <IconButton size="small" sx={{ mt: 0.5 }}>
                                  <DragIndicator fontSize="small" />
                                </IconButton>
                              </Box>
                            </ListItemIcon>

                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {inspection.name}
                                  {inspection.required ? (
                                    <Chip
                                      label="Required"
                                      color="error"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Chip
                                      label="Optional"
                                      color="default"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                inspection.dependencies?.length > 0
                                  ? `Dependencies: ${getDependencyNames(inspection.dependencies)}`
                                  : "No dependencies"
                              }
                            />

                            <ListItemSecondaryAction>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={inspection.required}
                                      onChange={() =>
                                        handleToggleRequired(inspection.id)
                                      }
                                    />
                                  }
                                  label="Required"
                                />
                                <Tooltip title="Move Up">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      moveInspection(inspection.id, "up")
                                    }
                                    disabled={index === 0}
                                  >
                                    ↑
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Move Down">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      moveInspection(inspection.id, "down")
                                    }
                                    disabled={
                                      index ===
                                      templateForm.inspections.length - 1
                                    }
                                  >
                                    ↓
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleRemoveInspection(inspection.id)
                                  }
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < templateForm.inspections.length - 1 && (
                            <Divider />
                          )}
                        </React.Fragment>
                      ))}
                  </List>
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={
              !templateForm.name || templateForm.inspections.length === 0
            }
            startIcon={<Save />}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpInspectionTemplatesPage;
